// src/services/search/workshopSearch.ts
/**
 * Сервис поиска для Workshop
 * Следует STYLEGUIDE.md и архитектуре глобального поиска
 */

import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { detectQueryType, calculateRelevance } from './utils';
import { DbWorkshop } from '@/types/entities/workshop';
import { dbWorkshopsToWorkshops } from '@/utils/mappers/workshopMappers';
import { SearchResult } from './types';

export const searchWorkshops = async (query: string, userId: string, limit: number = 5): Promise<SearchResult[]> => {
  if (!query.trim() || !userId) return [];

  const queryType = detectQueryType(query);
  // Narrow DB projection for selected columns
  type DbWorkshopPick = Pick<DbWorkshop,
    | 'id'
    | 'title'
    | 'description'
    | 'type'
    | 'status'
    | 'priority'
    | 'estimated_cost'
    | 'actual_cost'
    | 'total_cost'
    | 'client_name'
    | 'item_name'
    | 'service_name'
    | 'technician_name'
    | 'created_at'
    | 'urgent_work'
    | 'warranty_work'
  >;

  const baseSelect = () =>
    supabaseWorker
      .from('workshop')
      .select('id, title, description, type, status, priority, estimated_cost, actual_cost, total_cost, client_name, item_name, service_name, technician_name, created_at, urgent_work, warranty_work')
      .eq('user_id', userId);

  const queryBuilders = queryType === 'id'
    ? [
        baseSelect().eq('id', query).limit(limit),
        baseSelect().ilike('id', `${query}%`).limit(limit),
        baseSelect().ilike('title', `%${query}%`).limit(limit),
      ]
    : [
        baseSelect().ilike('title', `%${query}%`).limit(limit),
        baseSelect().ilike('description', `%${query}%`).limit(limit),
        baseSelect().ilike('client_name', `%${query}%`).limit(limit),
        baseSelect().ilike('item_name', `%${query}%`).limit(limit),
        baseSelect().ilike('service_name', `%${query}%`).limit(limit),
        baseSelect().ilike('technician_name', `%${query}%`).limit(limit),
      ];

  const collected: DbWorkshopPick[][] = [];
  for (const qb of queryBuilders) {
    const { data, error } = await qb;
    if (error) {
      // eslint-disable-next-line no-console
      console.error('[GlobalSearch][workshop] source error:', error);
      throw new Error('Workshop search source failed');
    }
    if (Array.isArray(data)) {
      collected.push(data as DbWorkshopPick[]);
    }
  }
  const rows: DbWorkshopPick[] = collected.flat();
  const unique = Array.from(new Map(rows.map(r => [r.id, r])).values()).slice(0, limit);

  // Преобразуем DB типы в Frontend типы согласно STYLEGUIDE.md
  const workshops = dbWorkshopsToWorkshops(unique as unknown as DbWorkshop[]);

  // Преобразуем Workshop в SearchResult
  return workshops.map((workshop): SearchResult => {
    const relevance = calculateRelevance(query, [
      workshop.title,
      workshop.description,
      workshop.clientName,
      workshop.itemName,
      workshop.serviceName,
      workshop.technicianName
    ], queryType);

    // Формируем подзаголовок на основе типа работы
    let subtitle = '';
    if (workshop.type === 'service' && workshop.serviceName) {
      subtitle = `Услуга: ${workshop.serviceName}`;
    } else if (workshop.type === 'repair' && workshop.itemName) {
      subtitle = `Ремонт: ${workshop.itemName}`;
    } else {
      subtitle = `${workshop.type === 'service' ? 'Услуга' : workshop.type === 'repair' ? 'Ремонт' : 'Работа'} • ${workshop.status}`;
    }

    if (workshop.clientName) {
      subtitle += ` • ${workshop.clientName}`;
    }

    return {
      id: workshop.id,
      type: 'workshop',
      title: workshop.title,
      subtitle,
      description: workshop.description,
      icon: workshop.type === 'service' ? 'Settings' : workshop.type === 'repair' ? 'Wrench' : 'Briefcase',
      url: `/workshop/${workshop.id}`,
      relevance,
      createdAt: workshop.createdAt.toISOString(),
      status: workshop.status,
    };
  });
};
